import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1633257245586-9addcf688fbe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
};

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const womenServices = [
  { name: 'Knotless Braids', price: 'From £120', desc: 'Lightweight, natural-looking braids with minimal tension on the scalp', duration: '4-6 hours' },
  { name: 'Box Braids', price: 'From £100', desc: 'Classic protective style with versatile styling options', duration: '4-5 hours' },
  { name: 'Cornrows', price: 'From £60', desc: 'Traditional African styling with intricate patterns', duration: '2-3 hours' },
  { name: 'Wig Installation', price: 'From £80', desc: 'Seamless wig fitting, styling and blending', duration: '1-2 hours' },
  { name: 'Wig Revamp', price: 'From £50', desc: 'Wash, restyle, and refresh your wig', duration: '1-2 hours' },
  { name: 'Natural Hair Styling', price: 'From £50', desc: 'Embrace and enhance your natural texture', duration: '1-2 hours' },
  { name: 'Silk Press', price: 'From £65', desc: 'Sleek, straightened look without chemical damage', duration: '1.5-2 hours' },
  { name: 'Relaxer', price: 'From £70', desc: 'Professional relaxer application and styling', duration: '2-3 hours' },
  { name: 'Hair Treatment', price: 'From £40', desc: 'Deep conditioning and restorative treatments', duration: '45 mins - 1 hour' },
  { name: 'Weave Installation', price: 'From £100', desc: 'Sew-in weave installation with blending', duration: '2-3 hours' },
  { name: 'Wash and Blow Dry', price: 'From £35', desc: 'Cleanse, condition, and style', duration: '45 mins - 1 hour' },
  { name: 'Bridal / Occasion Styling', price: 'From £150', desc: 'Special occasion hair for weddings and events', duration: 'Consultation required' },
  { name: 'Hair Colouring', price: 'From £80', desc: 'Professional colour application and styling', duration: '2-4 hours' },
];

const menServices = [
  { name: 'Haircut / Fade', price: 'From £25', desc: 'Precision cuts and fades tailored to your style', duration: '30-45 mins' },
  { name: 'Skin Fade', price: 'From £30', desc: 'Clean skin fade with expert blending', duration: '30-45 mins' },
  { name: 'Beard Trim', price: 'From £15', desc: 'Shape and define your beard', duration: '15-20 mins' },
  { name: 'Shape Up', price: 'From £15', desc: 'Clean up your hairline and edges', duration: '15-20 mins' },
  { name: 'Haircut & Beard Combo', price: 'From £35', desc: 'Complete grooming package', duration: '45 mins - 1 hour' },
  { name: 'Cornrows', price: 'From £50', desc: 'Classic cornrow styles for men', duration: '1-2 hours' },
  { name: 'Twists', price: 'From £45', desc: 'Two-strand or three-strand twists', duration: '1-2 hours' },
  { name: 'Locs Maintenance', price: 'From £60', desc: 'Retwisting, styling, and loc care', duration: '1-3 hours' },
  { name: 'Hair Wash', price: 'From £15', desc: 'Thorough cleanse and condition', duration: '20-30 mins' },
  { name: 'Scalp Treatment', price: 'From £30', desc: 'Nourishing scalp care treatment', duration: '30-45 mins' },
];

const addOnServices = [
  { name: 'Kids Hair (Girls)', price: 'From £40', desc: 'Braids, twists, and styles for children', duration: 'Varies' },
  { name: 'Kids Hair (Boys)', price: 'From £18', desc: 'Haircuts and styles for young boys', duration: '20-30 mins' },
  { name: 'Consultation', price: 'Free', desc: 'Discuss your hair goals and get expert advice', duration: '15-30 mins' },
  { name: 'Home Service', price: '+£50', desc: 'Bring the salon experience to your home', duration: 'Add-on' },
  { name: 'Group Booking', price: 'Contact us', desc: 'Special rates for group appointments', duration: 'Contact us' },
];

export const Services = () => {
  const [activeTab, setActiveTab] = useState('women');

  const tabs = [
    { id: 'women', label: "Women's Services" },
    { id: 'men', label: "Men's Services" },
    { id: 'addons', label: 'Add-ons & Special' },
  ];

  const getActiveServices = () => {
    switch (activeTab) {
      case 'women':
        return womenServices;
      case 'men':
        return menServices;
      case 'addons':
        return addOnServices;
      default:
        return womenServices;
    }
  };

  return (
    <main data-testid="services-page">
      {/* Page Header */}
      <section 
        className="page-header"
        style={{ 
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-[#0A0A0A]" />
        <div className="container-custom relative z-10 text-center pt-24">
          <motion.p 
            className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            What We Offer
          </motion.p>
          <motion.h1 
            className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#F5F5F5]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Our <span className="italic">Services</span>
          </motion.h1>
        </div>
      </section>

      {/* Services Content */}
      <section className="py-24 md:py-32 bg-[#F9F9F9]" data-testid="services-content">
        <div className="container-custom">
          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Service Cards */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="initial"
            animate="animate"
            variants={stagger}
            key={activeTab}
          >
            {getActiveServices().map((service, index) => (
              <motion.div
                key={service.name}
                className="bg-white p-8 border border-neutral-100 hover:border-[#C9A227]/50 hover:shadow-lg transition-all group"
                variants={fadeInUp}
                data-testid={`service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-heading text-xl text-[#2E2E2E] group-hover:text-[#C9A227] transition-colors">
                    {service.name}
                  </h3>
                  <span className="price-tag text-lg font-semibold">{service.price}</span>
                </div>
                <p className="text-[#666] text-sm mb-4 leading-relaxed">{service.desc}</p>
                <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                  <span className="text-[#999] text-xs uppercase tracking-wider">
                    {service.duration}
                  </span>
                  <Link 
                    to="/book" 
                    className="text-[#C9A227] text-sm uppercase tracking-wider hover:underline"
                  >
                    Book Now
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Note */}
          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-[#666] text-sm">
              * Prices may vary depending on hair length, thickness, and complexity. 
              A consultation is recommended for accurate pricing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="services-cta">
        <div className="container-custom">
          <motion.div 
            className="max-w-2xl mx-auto text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 
              className="font-heading text-4xl md:text-5xl text-[#F5F5F5] mb-6"
              variants={fadeInUp}
            >
              Not Sure What You <span className="italic text-[#C9A227]">Need</span>?
            </motion.h2>
            <motion.p 
              className="text-[#999] text-lg mb-10"
              variants={fadeInUp}
            >
              Book a free consultation with one of our stylists to discuss your hair goals 
              and find the perfect service for you.
            </motion.p>
            <motion.div className="flex flex-col sm:flex-row gap-4 justify-center" variants={fadeInUp}>
              <Link to="/book" data-testid="services-book-btn" className="btn-primary text-center">
                Book Appointment
              </Link>
              <Link to="/contact" data-testid="services-contact-btn" className="btn-secondary text-center">
                Contact Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Services;
